import { useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Download, Copy, FileText } from "lucide-react";
import { toast } from "sonner";
import { fmtAmt, fmtUsd, fmtPrice } from "../../lib/format";

const W = 1200, H = 630, PAD = 72;

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const drawCard = (canvas, deal) => {
  const ctx = canvas.getContext("2d");
  canvas.width = W; canvas.height = H;
  ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";

  ctx.fillStyle = "#000000"; ctx.fillRect(0, 0, W, H);
  const g1 = ctx.createRadialGradient(180, 120, 0, 180, 120, 540);
  g1.addColorStop(0, "rgba(192,38,245,0.30)"); g1.addColorStop(1, "rgba(192,38,245,0)");
  ctx.fillStyle = g1; ctx.fillRect(0, 0, W, H);
  const g2 = ctx.createRadialGradient(1060, 600, 0, 1060, 600, 540);
  g2.addColorStop(0, "rgba(107,33,168,0.32)"); g2.addColorStop(1, "rgba(107,33,168,0)");
  ctx.fillStyle = g2; ctx.fillRect(0, 0, W, H);

  ctx.strokeStyle = "rgba(255,255,255,0.035)"; ctx.lineWidth = 1;
  for (let x = 0; x <= W; x += 48) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
  for (let y = 0; y <= H; y += 48) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

  ctx.strokeStyle = "rgba(255,255,255,0.10)"; ctx.lineWidth = 2; roundRect(ctx, 20, 20, W - 40, H - 40, 20); ctx.stroke();

  // brand
  ctx.fillStyle = "#FFFFFF"; ctx.font = "700 40px 'Space Grotesk', sans-serif";
  ctx.fillText("CROSS", PAD, 96);
  ctx.fillStyle = "#8C8C96"; ctx.font = "500 20px 'IBM Plex Mono', monospace";
  ctx.fillText("OTC ESCROW · SETTLED OFF-MARKET", PAD, 126);

  // settled pill
  const pill = "SETTLED"; ctx.font = "600 22px 'IBM Plex Mono', monospace";
  const pw = ctx.measureText(pill).width + 40;
  roundRect(ctx, W - PAD - pw, 66, pw, 40, 20);
  ctx.fillStyle = "rgba(16,185,129,0.14)"; ctx.fill();
  ctx.strokeStyle = "rgba(16,185,129,0.5)"; ctx.lineWidth = 1.5; roundRect(ctx, W - PAD - pw, 66, pw, 40, 20); ctx.stroke();
  ctx.fillStyle = "#10B981"; ctx.fillText(pill, W - PAD - pw + 20, 93);

  // token / pair
  ctx.fillStyle = "#FFFFFF"; ctx.font = "700 88px 'Space Grotesk', sans-serif";
  ctx.fillText(deal.token_symbol, PAD, 232);
  const tw = ctx.measureText(deal.token_symbol).width;
  ctx.fillStyle = "#C026F5"; ctx.font = "700 52px 'Space Grotesk', sans-serif";
  ctx.fillText(`/${deal.stock}`, PAD + tw + 18, 232);
  ctx.fillStyle = "#8C8C96"; ctx.font = "400 24px Inter, sans-serif";
  ctx.fillText("Block settled at one flat price — the public pool never saw it.", PAD, 274);

  // hero: impact saved
  ctx.fillStyle = "#8C8C96"; ctx.font = "500 22px 'IBM Plex Mono', monospace";
  ctx.fillText("PRICE-IMPACT SAVED VS PUBLIC POOL", PAD, 336);
  ctx.fillStyle = "#FFFFFF"; ctx.font = "700 96px 'Space Grotesk', sans-serif";
  const saved = `${deal.slippage.saved_usd >= 0 ? "+" : "−"}${fmtUsd(Math.abs(deal.slippage.saved_usd))}`;
  const sw = ctx.measureText(saved).width;
  ctx.save();
  ctx.shadowColor = "rgba(192,38,245,0.85)"; ctx.shadowBlur = 42;
  ctx.fillText(saved, PAD, 436);
  ctx.restore();
  ctx.fillStyle = "#C026F5"; ctx.font = "600 30px 'IBM Plex Mono', monospace";
  ctx.fillText(`${deal.slippage.impact_pct.toFixed(1)}% avoided`, PAD + sw + 26, 436);

  // metric columns
  const cols = [PAD, PAD + 380, PAD + 760];
  const metrics = [
    ["SIZE", `${fmtAmt(deal.token_amount)} ${deal.token_symbol}`],
    ["PRICE", `${fmtPrice(deal.price_usd)}/M`],
    ["NOTIONAL", fmtUsd(deal.notional_usd)],
  ];
  metrics.forEach(([l, v], i) => {
    ctx.fillStyle = "#8C8C96"; ctx.font = "500 20px 'IBM Plex Mono', monospace"; ctx.fillText(l, cols[i], 506);
    ctx.fillStyle = "#FFFFFF"; ctx.font = "500 34px 'IBM Plex Mono', monospace"; ctx.fillText(v, cols[i], 546);
  });

  ctx.fillStyle = "#8C8C96"; ctx.font = "500 19px 'IBM Plex Mono', monospace";
  ctx.fillText(`${deal.id}   ·   Robinhood Chain 4663   ·   Uniswap v4   ·   ${deal.stock} peg unmoved`, PAD, 590);
};

const summaryText = (d) =>
  `${d.token_symbol}/${d.stock} · ${fmtAmt(d.token_amount)} tokens settled OTC on Cross at ${fmtPrice(d.price_usd)}/M — ${fmtUsd(d.slippage.saved_usd)} price-impact saved vs the public pool. Off-market escrow, no pool touched. ${d.id}`;

export const ShareCard = ({ deal, onClose }) => {
  const canvasRef = useRef(null);
  const open = !!deal;

  // Callback ref: draws the instant the canvas node attaches (survives Radix's
  // portal/animation mount timing). Draws immediately, then re-draws once web
  // fonts are ready so the mono/heading faces render crisply.
  const attach = useCallback((node) => {
    canvasRef.current = node;
    if (!node || !deal) return;
    try { drawCard(node, deal); } catch (e) { console.error("Share card draw failed:", e); }
    (document.fonts?.ready || Promise.resolve()).then(() => {
      if (canvasRef.current === node) { try { drawCard(node, deal); } catch (e) { console.error("Share card font redraw failed:", e); } }
    });
  }, [deal]);

  const download = () => {
    canvasRef.current?.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `cross-${deal.id}.png`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Card downloaded");
    }, "image/png");
  };

  const copyImage = () => {
    if (!window.ClipboardItem || !navigator.clipboard?.write) { toast.error("Image copy unsupported here — use Download"); return; }
    canvasRef.current?.toBlob(async (blob) => {
      try { await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]); toast.success("Card image copied to clipboard"); }
      catch { toast.error("Couldn't copy image — use Download instead"); }
    }, "image/png");
  };

  const copyText = async () => {
    try { await navigator.clipboard.writeText(summaryText(deal)); toast.success("Summary copied"); }
    catch { toast.error("Couldn't copy text"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-s1 border-line text-slate-100 sm:max-w-2xl" data-testid="share-card-modal">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Share settled deal</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            Off-market proof — token, size and the price impact this block avoided vs the public pool.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md overflow-hidden border border-line2">
          <canvas ref={attach} className="w-full h-auto block" data-testid="share-card-canvas" />
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <button onClick={download} className="btn-primary flex-1 min-w-[140px]" data-testid="share-download-btn"><Download size={15} /> Download PNG</button>
          <button onClick={copyImage} className="btn-ghost flex-1 min-w-[130px]" data-testid="share-copy-image-btn"><Copy size={15} /> Copy image</button>
          <button onClick={copyText} className="btn-ghost flex-1 min-w-[130px]" data-testid="share-copy-text-btn"><FileText size={15} /> Copy text</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
